import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { ArrowLeft, Loader2, CheckCircle, Stethoscope, Heart, Shield, FileText, Clock, Users, User, Mail, Lock, Phone, Eye, EyeOff } from 'lucide-react';
import { apiRequest } from '../lib/utils';
import { toast } from 'sonner';

const PHYSIGN_LOGO = "https://customer-assets.emergentagent.com/job_4bf5f59f-ff2a-4b89-ab5f-09ffb3b85c36/artifacts/91dqnx5f_high-resolution-color-logo-3.png";

export default function ProviderSignup() {
  const navigate = useNavigate();

  const [step, setStep] = useState('role'); // role, details
  const [role, setRole] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Registration fields
  const [regName, setRegName] = useState('');
  const [regPhone, setRegPhone] = useState('');
  const [regEmail, setRegEmail] = useState('');
  const [regPassword, setRegPassword] = useState('');

  const validatePhone = (num) => {
    const clean = num.replace(/\D/g, '');
    return clean.length === 10 && /^[6-9]/.test(clean);
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!regName.trim()) { toast.error('Name enter karein'); return; }
    if (!validatePhone(regPhone)) { toast.error('Valid 10-digit phone number enter karein'); return; }
    if (!regEmail) { toast.error('Email enter karein'); return; }
    if (!regPassword || regPassword.length < 6) { toast.error('Password minimum 6 characters hona chahiye'); return; }
    setLoading(true);
    try {
      const data = await apiRequest('/auth/register', {
        method: 'POST',
        body: JSON.stringify({
          name: regName,
          phone: regPhone,
          email: regEmail,
          password: regPassword,
          role: role,
        }),
      });
      localStorage.setItem('token', data.token);
      toast.success('Registration successful! Complete your profile.');
      window.location.href = '/provider';
    } catch (error) { toast.error(error.message); }
    finally { setLoading(false); }
  };

  const roles = [
    {
      id: 'doctor',
      title: 'Doctor',
      icon: Stethoscope,
      color: 'bg-blue-500',
      borderColor: 'border-blue-500',
      bgColor: 'bg-blue-50',
      description: 'Register as a verified doctor on Physign',
      benefits: [
        { icon: Users, text: 'Reach thousands of patients' },
        { icon: Clock, text: 'Manage your schedule online' },
        { icon: Shield, text: 'Get verified badge' },
      ],
    },
    {
      id: 'nurse',
      title: 'Nurse',
      icon: Heart,
      color: 'bg-rose-500',
      borderColor: 'border-rose-500',
      bgColor: 'bg-rose-50',
      description: 'Register as a professional nurse on Physign',
      benefits: [
        { icon: Users, text: 'Connect with patients directly' },
        { icon: FileText, text: 'Showcase your qualifications' },
        { icon: Shield, text: 'Get verified & trusted' },
      ],
    },
  ];

  const selectedRole = roles.find(r => r.id === role);

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col" data-testid="provider-signup-page">
      {/* Header */}
      <header className="py-4 px-6 bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <span className="text-xl font-bold text-[#3B1C73]" style={{ fontFamily: 'Plus Jakarta Sans' }}>Physign</span>
          </Link>
          <Link to="/auth" className="text-sm text-[#0F62FE] hover:underline font-medium" data-testid="patient-login-link">
            Patient Login
          </Link>
        </div>
      </header>

      {/* Main */}
      <main className="flex-1 flex items-center justify-center p-4 py-8">
        {step === 'role' && (
          <div className="w-full max-w-2xl" data-testid="role-selection-step">
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Plus Jakarta Sans' }}>
                Join Physign as a Provider
              </h1>
              <p className="text-slate-500">Select your role to get started</p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {roles.map((r) => (
                <Card
                  key={r.id}
                  className={`cursor-pointer transition-all hover:shadow-lg border-2 ${role === r.id ? `${r.borderColor} shadow-lg` : 'border-slate-200 hover:border-slate-300'}`}
                  onClick={() => setRole(r.id)}
                  data-testid={`role-card-${r.id}`}
                >
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className={`w-12 h-12 ${r.color} rounded-xl flex items-center justify-center`}>
                        <r.icon className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-slate-900">{r.title}</h3>
                        <p className="text-sm text-slate-500">{r.description}</p>
                      </div>
                    </div>
                    <div className="space-y-2.5 mt-4">
                      {r.benefits.map((b, i) => (
                        <div key={i} className="flex items-center gap-2.5">
                          <div className={`w-7 h-7 ${r.bgColor} rounded-lg flex items-center justify-center flex-shrink-0`}>
                            <b.icon className={`w-3.5 h-3.5 ${r.id === 'doctor' ? 'text-blue-600' : 'text-rose-600'}`} />
                          </div>
                          <span className="text-sm text-slate-600">{b.text}</span>
                        </div>
                      ))}
                    </div>
                    {role === r.id && (
                      <div className={`mt-4 flex items-center justify-center gap-1.5 ${r.id === 'doctor' ? 'text-blue-600' : 'text-rose-600'}`}>
                        <CheckCircle className="w-4 h-4" />
                        <span className="text-sm font-medium">Selected</span>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="mt-8 flex justify-center">
              <Button
                className="h-12 px-8 bg-[#0F62FE] hover:bg-[#0050E6] text-white rounded-lg"
                disabled={!role}
                onClick={() => setStep('details')}
                data-testid="continue-to-phone-btn"
              >
                Continue as {role === 'doctor' ? 'Doctor' : role === 'nurse' ? 'Nurse' : '...'}
              </Button>
            </div>

            <p className="text-center mt-4 text-sm text-slate-500">
              Already registered?{' '}
              <Link to="/auth?role=provider" className="text-[#0F62FE] hover:underline font-medium" data-testid="already-registered-link">
                Login here
              </Link>
            </p>
          </div>
        )}

        {step === 'details' && (
          <Card className="w-full max-w-md shadow-lg border-slate-100" data-testid="details-step">
            <CardHeader className="text-center">
              <div className="flex justify-center mb-3">
                <div className={`w-14 h-14 ${selectedRole?.color} rounded-2xl flex items-center justify-center`}>
                  {selectedRole && <selectedRole.icon className="w-7 h-7 text-white" />}
                </div>
              </div>
              <CardTitle className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>
                {role === 'doctor' ? 'Doctor' : 'Nurse'} Registration
              </CardTitle>
              <CardDescription>Fill in your details to create your account</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleRegister} className="space-y-4">
                <button type="button" onClick={() => setStep('role')} className="flex items-center text-sm text-slate-500 hover:text-slate-700">
                  <ArrowLeft className="w-4 h-4 mr-1" />Change Role
                </button>

                <div>
                  <Label>Full Name *</Label>
                  <div className="relative mt-1">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      type="text"
                      placeholder="Enter your full name"
                      value={regName}
                      onChange={(e) => setRegName(e.target.value)}
                      className="pl-10"
                      data-testid="provider-reg-name"
                      required
                    />
                  </div>
                </div>

                <div>
                  <Label>Phone Number *</Label>
                  <div className="flex gap-2 mt-1">
                    <div className="w-16 h-10 px-2 border border-slate-200 rounded-lg bg-slate-50 flex items-center justify-center text-sm font-medium text-slate-700">+91</div>
                    <Input
                      type="tel"
                      placeholder="10-digit phone number"
                      value={regPhone}
                      onChange={(e) => setRegPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                      className={`flex-1 ${regPhone && !validatePhone(regPhone) ? 'border-red-400' : ''}`}
                      maxLength={10}
                      data-testid="provider-reg-phone"
                      required
                    />
                  </div>
                  {regPhone && !validatePhone(regPhone) && (
                    <p className="text-xs text-red-500 mt-1">Valid 10-digit Indian number enter karein (6/7/8/9 se start hona chahiye)</p>
                  )}
                </div>

                <div>
                  <Label>Email *</Label>
                  <div className="relative mt-1">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      type="email"
                      placeholder="doctor@example.com"
                      value={regEmail}
                      onChange={(e) => setRegEmail(e.target.value)}
                      className="pl-10"
                      data-testid="provider-reg-email"
                      required
                    />
                  </div>
                </div>

                <div>
                  <Label>Password *</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Minimum 6 characters"
                      value={regPassword}
                      onChange={(e) => setRegPassword(e.target.value)}
                      className="pl-10 pr-10"
                      data-testid="provider-reg-password"
                      required
                      minLength={6}
                    />
                    <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" onClick={() => setShowPassword(!showPassword)}>
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {regPassword && regPassword.length < 6 && (
                    <p className="text-xs text-red-500 mt-1">Minimum 6 characters required</p>
                  )}
                </div>

                <Button type="submit" className="w-full h-11 bg-[#0F62FE] hover:bg-[#0050E6] disabled:bg-slate-300 disabled:cursor-not-allowed" disabled={loading || !regName || !validatePhone(regPhone) || !regEmail || regPassword.length < 6} data-testid="provider-reg-submit">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}Create {role === 'doctor' ? 'Doctor' : 'Nurse'} Account
                </Button>
              </form>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
